/**
 * Help Modal Component
 * Displays contextual help for node types and categories
 */

import React from 'react';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { ModalPortal } from './ModalPortal';
import { useTopmostEscape, useDialogFocus } from '../Support/Modal';
import { DocumentIcon } from '@bizfirst/common-react';
import type { NodeType, NodeCategory } from '@flow-studio/types';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  entity: NodeType | NodeCategory;
  entityType: 'nodeType' | 'category';
}

export const HelpModal: React.FC<HelpModalProps> = ({
  isOpen,
  onClose,
  entity,
  entityType,
}) => {
  const { theme } = useUIStore();
  const themeClass = theme === 'light' ? 'light' : '';

  // Escape closes only the top-most open dialog (see useTopmostEscape). Called before the early
  // return below so the hook order stays the same whether or not the dialog is open.
  const dialogRef = React.useRef<HTMLDivElement>(null);
  useTopmostEscape(isOpen, onClose, dialogRef);
  const titleId = React.useId();
  // Same dialog semantics and focus handling as <Modal>: focus moves in on open, Tab stays inside,
  // and focus returns to the opener on close.
  useDialogFocus(dialogRef, isOpen);

  if (!isOpen) return null;

  // Determine default images
  const defaultIcon = entityType === 'category'
    ? '/assets/images/standard/DefaultNodeCategoryIcon.png'
    : '/assets/images/standard/DefaultNodeTypeIcon.png';

  const defaultImage = entityType === 'category'
    ? '/assets/images/standard/DefaultNodeCategoryImage.png'
    : '/assets/images/standard/DefaultNodeTypeImage.png';

  const displayIcon = entity.iconUrl || defaultIcon;
  const displayImage = entity.imageUrl || defaultImage;

  // Construct community URL for Learn More
  const nodeCode = entityType === 'nodeType' ? (entity as NodeType).code : (entity as NodeCategory).code;
  const communityUrl = `https://community.bizfirstai.com/t/node-home-${nodeCode}`;

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={onClose} style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className={`modal ${themeClass}`}
          style={{
            width: '100vw',
            height: '100vh',
            maxWidth: '100vw',
            maxHeight: '100vh',
            margin: 0,
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="modal-header-plain">
            <div id={titleId} className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {displayIcon && (
                <span style={{ fontSize: '24px' }}>{displayIcon}</span>
              )}
              {entity.displayName}
            </div>
            {/* Learn More Button */}
            <a
              href={communityUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm"
              style={{
                color: '#3A8C45',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 500,
                padding: '6px 12px',
                borderRadius: '6px',
                backgroundColor: 'rgba(58, 140, 69, 0.1)',
                border: '1px solid rgba(58, 140, 69, 0.3)',
                marginRight: '12px',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'rgba(58, 140, 69, 0.2)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = 'rgba(58, 140, 69, 0.1)';
              }}
            >
              Learn More
            </a>
            <button className="modal-close-button" onClick={onClose}>
              ×
            </button>
          </div>

          {/* Body */}
          <div className="modal-body" style={{
            flex: 1,
            overflowY: 'auto',
            padding: '32px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div style={{ maxWidth: '1200px', width: '100%' }}>
            {/* Short Description */}
            {entity.shortDescription && (
              <div className="modal-info-section" style={{ marginBottom: '24px' }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: '18px',
                  marginBottom: '12px',
                  color: theme === 'dark' ? '#f3f4f6' : '#111827',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}>
                  <img
                    src={displayIcon}
                    alt=""
                    style={{
                      width: '32px',
                      height: '32px',
                      objectFit: 'contain',
                    }}
                    onError={(e) => {
                      e.currentTarget.src = defaultIcon;
                    }}
                  />
                  Overview
                </div>
                <div style={{
                  fontSize: '16px',
                  lineHeight: '1.8',
                  color: theme === 'dark' ? '#e5e7eb' : '#374151',
                }}>
                  {entity.shortDescription}
                </div>
              </div>
            )}

            {/* Image */}
            {displayImage && (
              <div style={{ marginBottom: '32px', width: '100%' }}>
                <img
                  src={displayImage}
                  alt={entity.displayName}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '12px',
                    border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e5e7eb'}`,
                    boxShadow: theme === 'dark'
                      ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                      : '0 4px 20px rgba(0, 0, 0, 0.1)',
                  }}
                  onError={(e) => {
                    // Fallback to default if image fails to load
                    e.currentTarget.src = defaultImage;
                  }}
                />
              </div>
            )}

            {/* Long Description */}
            {entity.longDescription && (
              <div className="modal-info-section" style={{ marginBottom: '24px' }}>
                <div style={{
                  fontWeight: 600,
                  fontSize: '18px',
                  marginBottom: '12px',
                  color: theme === 'dark' ? '#f3f4f6' : '#111827',
                }}>
                  Details
                </div>
                <div style={{
                  fontSize: '15px',
                  lineHeight: '1.8',
                  whiteSpace: 'pre-wrap',
                  color: theme === 'dark' ? '#e5e7eb' : '#374151',
                }}>
                  {entity.longDescription}
                </div>
              </div>
            )}

            {/* Fallback if no help content */}
            {!entity.shortDescription && !entity.longDescription && !entity.helpUrl && (
              <div style={{
                padding: '48px',
                textAlign: 'center',
                color: theme === 'dark' ? '#9ca3af' : '#6b7280',
                fontSize: '16px',
              }}>
                <div style={{ marginBottom: '16px' }}><DocumentIcon size={64} /></div>
                <div>No detailed help available yet.</div>
                {entity.description && (
                  <div style={{ marginTop: '16px', fontStyle: 'italic', fontSize: '15px' }}>
                    {entity.description}
                  </div>
                )}
              </div>
            )}
            </div>
          </div>

          {/* Footer */}
          <div className={`modal-footer ${themeClass}`}>
            <button
              className={`modal-button secondary ${themeClass}`}
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
